import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiXMark as CloseIcon, HiPlus as PlusIcon } from 'react-icons/hi2';
import { Button, Select } from '@knack/asterisk-react';
import { produce } from 'immer';
import snakeCase from 'lodash.snakecase';

import { type KnackField, type KnackFieldKey } from '@/types/schema/KnackField';
import { filterOperatorsByType } from '@/types/schema/operators/FilterOperatorsByType';
import { cn } from '@/utils/tailwind';
import { BooleanInputValue } from '@/components/data-table/display/header/filters/inputs/BooleanInputValue';
import { DefaultInputValue } from '@/components/data-table/display/header/filters/inputs/DefaultInputValue';
import { MultipleChoiceInputValue } from '@/components/data-table/display/header/filters/inputs/MultipleChoiceInputValue';
import { MatchSelector } from '@/components/data-table/display/header/filters/MatchSelector';
import { type FilterFormat, type RuleFormat } from '@/components/data-table/helpers/getRecords';
import { useDataTableStore } from '@/components/data-table/useDataTableStore';
import { FieldSelect } from '@/components/FieldSelect';

export type InputValueType<T> = {
  defaultValue: string;
  onChange: (value: string) => void;
  field: T;
};

const operatorsWithoutInputValue = ['is blank', 'is not blank', 'is any'];

export function FilterInputValue(
  props: InputValueType<any> & { rule: RuleFormat; field: KnackField }
) {
  const { rule, field } = props;
  // Some operators don't require an input value
  if (operatorsWithoutInputValue.includes(rule.operator)) return null;
  // If the field type doesn't have operators, return null. Used for fields that are not yet supported
  if (!filterOperatorsByType[field.type]) return null;

  if (field.type === 'multiple_choice') return <MultipleChoiceInputValue {...props} />;
  if (field.type === 'boolean') return <BooleanInputValue {...props} />;

  // TODO: FE-2522 Handle Date/time Input types (weekly, datepicker, frequency...)
  // TODO: FE-2522 Handle Connection Input

  return <DefaultInputValue {...props} />;
}

export function getFirstOperator(fields: KnackField[], fieldKey: string): string {
  const field = fields.find((el) => el.key === fieldKey);

  if (!field) return '';

  return filterOperatorsByType[field.type]?.[0] || '';
}

export function OperatorSelect({
  field,
  ruleIndex,
  rule,
  onValueChange
}: {
  field: KnackField;
  ruleIndex: number;
  rule: RuleFormat;
  onValueChange: (operatorSelected: string) => void;
}) {
  const [t] = useTranslation();

  if (!filterOperatorsByType[field.type]) {
    return <p className="w-full pl-4">{t('keywords.coming_soon')}</p>;
  }

  return (
    <Select onValueChange={onValueChange} defaultValue={rule.operator} value={rule.operator}>
      <Select.Trigger
        placeholder={t('components.data_table.filtering.select')}
        className="w-full rounded-lg"
        data-testid={`filter-rule-${ruleIndex}-operator-select`}
      />
      <Select.Content className="max-h-[200px]">
        {filterOperatorsByType[field.type]?.map((operator) => (
          <Select.Item key={operator} value={operator}>
            {t(`operators.${snakeCase(operator)}`)}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  );
}

export function Filters({
  onCancel,
  filterChange,
  initialFilters
}: {
  onCancel: () => void;
  filterChange: (format: FilterFormat | null) => void;
  initialFilters: FilterFormat;
}) {
  const [t] = useTranslation();
  const fields = useDataTableStore().use.fields();
  const [currentFilter, setCurrentFilter] = useState<FilterFormat>(initialFilters);

  return (
    <form
      className="flex flex-col justify-between"
      onSubmit={() => {
        // Remove the filter if there are no conditions
        if (currentFilter.rules.length === 0) {
          filterChange(null);
          return;
        }
        filterChange(currentFilter);
      }}
    >
      {currentFilter.rules.map((rule, ruleIndex) => {
        const currentField = fields.find((el) => el.key === rule.field);

        if (!currentField) return null;

        return (
          <div
            // We don't have a unique key, so we have to use the index of the array
            // We also add other rule options to ensure it re-renders when those change
            // eslint-disable-next-line react/no-array-index-key
            key={`${ruleIndex}-${rule.operator}-${rule.field}`}
            className="mb-5 flex items-center gap-2 text-sm"
          >
            <MatchSelector
              ruleIndex={ruleIndex}
              currentFilter={currentFilter}
              setCurrentFilter={setCurrentFilter}
            />
            <FieldSelect
              id="field-select"
              data-testid={`filter-rule-${ruleIndex}-field-select`}
              fields={fields}
              defaultValue={rule.field}
              onFieldChange={(fieldSelected) => {
                const nextState = produce(currentFilter, (draftState) => {
                  draftState.rules[ruleIndex].field = fieldSelected as KnackFieldKey;
                  draftState.rules[ruleIndex].value = '';
                  draftState.rules[ruleIndex].operator = getFirstOperator(fields, fieldSelected);
                });

                setCurrentFilter(nextState);
              }}
            />
            <OperatorSelect
              {...{
                field: currentField,
                ruleIndex,
                rule,
                onValueChange: (operatorSelected) => {
                  const nextState = produce(currentFilter, (draftState) => {
                    draftState.rules[ruleIndex].operator = operatorSelected;
                  });

                  setCurrentFilter(nextState);
                }
              }}
            />
            <FilterInputValue
              {...{
                rule,
                field: currentField,
                defaultValue: rule.value,
                onChange: (value) => {
                  const nextState = produce(currentFilter, (draftState) => {
                    draftState.rules[ruleIndex].value = value;
                  });
                  setCurrentFilter(nextState);
                }
              }}
            />
            <Button
              intent="minimal"
              data-testid={`filter-rule-${ruleIndex}-remove`}
              onClick={() => {
                // Remove condition
                const nextState = produce(currentFilter, (draftState) => {
                  draftState.rules.splice(ruleIndex, 1);
                });

                setCurrentFilter(nextState);
              }}
            >
              <CloseIcon size={17} />
            </Button>
          </div>
        );
      })}
      <div>
        <Button
          data-testid="filter-add-condition"
          intent="secondary"
          className={cn(' w-auto', { 'ml-[78px]': Object.keys(currentFilter.rules).length > 0 })}
          onClick={() => {
            const nextState = produce(currentFilter, (draftState) => {
              draftState.rules.push({
                field: fields[0].key,
                operator: getFirstOperator(fields, fields[0].key),
                value: ''
              });
            });

            setCurrentFilter(nextState);
          }}
        >
          <PlusIcon className="mr-2" /> {t('components.data_table.filtering.condition')}
        </Button>
      </div>
      <div className="mt-4 flex justify-between gap-2">
        <Button
          className="text-default"
          data-testid="filter-remove"
          intent="minimal"
          onClick={() => {
            filterChange(null);
          }}
        >
          {t('components.data_table.filtering.remove')}
        </Button>
        <div className="flex gap-2">
          <Button
            data-testid="filter-cancel"
            intent="minimal"
            onClick={() => {
              onCancel();
            }}
          >
            {t('components.data_table.filtering.cancel')}
          </Button>
          <Button type="submit" data-testid="filter-apply">
            {t('components.data_table.filtering.filter')}
          </Button>
        </div>
      </div>
    </form>
  );
}
